import { legalFallback } from '@/lib/normalizeLegalDocument'
import { PATHS } from '@/routes/paths'
import { buildBlogCategorySeo, buildBlogListSeo } from './generators/blog'
import { buildHomeSeo } from './generators/home'
import { buildPropertyHref } from '@/lib/propertyNavLinks'
import { isSeoLandingPathname } from '@/lib/seoLandingPath'
import { buildListingSeoDescription, buildListingSeoKeywords, buildListingSeoTitle } from './generators/listing'
import { buildMediaListSeo } from './generators/media'
import { buildPropertySeoFromSlug } from './generators/property'
import { buildPrivateDashboardSeo, STATIC_PAGE_SEO } from './generators/static'
import { resolveSeo } from './resolve'
import type { ResolvedSeo } from './types'
import { absoluteUrl, listingCanonical, titleCase, truncateMetaDescription } from './utils'

function slugToLabel(slug: string): string {
  return titleCase(slug.replace(/^\d+-/, '').replace(/-/g, ' ')) || 'Page'
}

function buildBlogSeoFromSlug(slug: string) {
  const label = slugToLabel(slug)
  return {
    title: `${label} | QBC Blog`,
    description: truncateMetaDescription(
      `Read ${label} on the Quick Build Con blog — expert real estate insights, guides and market updates.`,
    ),
    canonical: absoluteUrl(PATHS.blogDetail(slug)),
    ogType: 'article',
  }
}

function buildMagazineSeoFromSlug(slug: string) {
  const label = slugToLabel(slug)
  return {
    title: `${label} | QBC E-Magazine`,
    description: truncateMetaDescription(
      `Read ${label} in the QBC digital magazine — property trends, market insights and investment guides.`,
    ),
    canonical: absoluteUrl(PATHS.magazineDetail(slug)),
  }
}

function buildMediaSeoFromSlug(slug: string) {
  const label = slugToLabel(slug)
  return {
    title: `${label} | QBC Media Center`,
    description: truncateMetaDescription(
      `Watch ${label} — property insights and expert advice from Quick Build Con.`,
    ),
    canonical: absoluteUrl(PATHS.mediaDetail(slug)),
  }
}

function buildPremiumProductSeoFromId(id: string) {
  return {
    title: `Premium Product ${id} | QBC Premium`,
    description: truncateMetaDescription(
      'Explore QBC Verified Promotion packages to sell or market your property faster across India.',
    ),
    canonical: absoluteUrl(PATHS.premiumProduct(id)),
  }
}

function buildPropertiesRouteSeo(search: string): ResolvedSeo {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const filters = {
    city: params.get('city') ?? undefined,
    intent: (params.get('intent') as 'buy' | 'rent' | 'commercial' | undefined) ?? undefined,
    area: params.get('area') ? [params.get('area')!] : undefined,
    category: params.get('category') ? [params.get('category')!] : undefined,
    bhk: params.get('bhk') ? [params.get('bhk')!] : undefined,
    search: params.get('search') ?? undefined,
  }

  const href = buildPropertyHref(
    filters.intent === 'rent'
      ? 'rent'
      : filters.intent === 'buy'
        ? 'buy'
        : filters.intent === 'commercial'
          ? 'commercial'
          : '',
    {
      city: filters.city,
      category: filters.category?.[0],
      area: filters.area?.[0],
      bhk: filters.bhk?.[0],
      search: filters.search,
    },
  )
  const qIndex = href.indexOf('?')
  const path = qIndex >= 0 ? href.slice(0, qIndex) : href
  const qs = qIndex >= 0 ? href.slice(qIndex + 1) : ''

  return resolveSeo({
    title: `Quick Build Con | ${buildListingSeoTitle(filters)}`,
    description: buildListingSeoDescription(filters, 0),
    keywords: buildListingSeoKeywords(filters),
    canonical: listingCanonical(path, qs),
  })
}

const STATIC_ROUTE_SEO: Record<string, () => Partial<ResolvedSeo>> = {
  [PATHS.home]: buildHomeSeo,
  [PATHS.about]: () => STATIC_PAGE_SEO.about,
  [PATHS.aboutAlt]: () => STATIC_PAGE_SEO.about,
  [PATHS.aboutusDir]: () => STATIC_PAGE_SEO.about,
  [PATHS.contact]: () => STATIC_PAGE_SEO.contact,
  [PATHS.contactAlt]: () => STATIC_PAGE_SEO.contact,
  [PATHS.faq]: () => STATIC_PAGE_SEO.faq,
  [PATHS.feedback]: () => STATIC_PAGE_SEO.feedback,
  [PATHS.achievements]: () => STATIC_PAGE_SEO.achievements,
  [PATHS.premium]: () => STATIC_PAGE_SEO.premiumProducts,
  [PATHS.calculator]: () => STATIC_PAGE_SEO.calculator,
  [PATHS.realEstateBook]: () => STATIC_PAGE_SEO.realEstateBook,
  [PATHS.homeLoanEmi]: () => STATIC_PAGE_SEO.homeLoanEmi(),
  [PATHS.homeLoanEligibility]: () => STATIC_PAGE_SEO.homeLoanEligibility(),
  [PATHS.premiumBenefitsTypo]: () => STATIC_PAGE_SEO.premiumProducts,
  [PATHS.magazine]: () => STATIC_PAGE_SEO.magazineList,
  [PATHS.blog]: () => buildBlogListSeo(),
  [PATHS.media]: () => buildMediaListSeo(),
  [PATHS.addProperty]: () => ({ ...STATIC_PAGE_SEO.postProperty, noIndex: true, noFollow: true }),
  [PATHS.premiumCourseList]: () => STATIC_PAGE_SEO.coursesList,
  [PATHS.premiumBenefit]: () => STATIC_PAGE_SEO.coursesList,
  '/purchase_history': () => STATIC_PAGE_SEO.coursesList,
  '/paymentdetails': () => STATIC_PAGE_SEO.coursesList,
}

const DASHBOARD_ROUTES: Record<string, string> = {
  [PATHS.dashboard]: 'Dashboard',
  [PATHS.editProfile]: 'Edit Profile',
  [PATHS.myWishlist]: 'Saved Properties',
  [PATHS.myProperty]: 'My Properties',
  [PATHS.myInquiry]: 'My Inquiries',
  [PATHS.mySiteVisit]: 'Site Visits',
  [PATHS.myOrders]: 'Orders & Payments',
  [PATHS.myPayments]: 'Orders & Payments',
}

/** Immediate route-based SEO before lazy pages or API data load. */
export function resolveRouteSeo(pathname: string, search = ''): ResolvedSeo {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'

  const propertyMatch = normalizedPath.match(/^\/property\/([^/]+)$/)
  if (propertyMatch) {
    return resolveSeo(buildPropertySeoFromSlug(propertyMatch[1]))
  }

  const blogCategoryMatch = normalizedPath.match(/^\/blog\/category\/([^/]+)$/)
  if (blogCategoryMatch) {
    return resolveSeo(buildBlogCategorySeo(blogCategoryMatch[1]))
  }

  const blogAltCategoryMatch = normalizedPath.match(/^\/blogs\/category\/([^/]+)$/)
  if (blogAltCategoryMatch) {
    return resolveSeo(buildBlogCategorySeo(blogAltCategoryMatch[1]))
  }

  const blogMatch = normalizedPath.match(/^\/blog\/([^/]+)$/)
  if (blogMatch) {
    return resolveSeo(buildBlogSeoFromSlug(blogMatch[1]))
  }

  const blogAltMatch = normalizedPath.match(/^\/blogs\/([^/]+)$/)
  if (blogAltMatch) {
    return resolveSeo(buildBlogSeoFromSlug(blogAltMatch[1]))
  }

  if (normalizedPath === PATHS.blogAlt) {
    return resolveSeo(buildBlogListSeo())
  }

  const magazineMatch = normalizedPath.match(/^\/magazine\/([^/]+)$/)
  if (magazineMatch) {
    return resolveSeo(buildMagazineSeoFromSlug(magazineMatch[1]))
  }

  const mediaMatch = normalizedPath.match(/^\/media\/([^/]+)$/)
  if (mediaMatch) {
    return resolveSeo(buildMediaSeoFromSlug(mediaMatch[1]))
  }

  const premiumProductMatch = normalizedPath.match(/^\/ourproducts\/([^/]+)$/)
  if (premiumProductMatch) {
    return resolveSeo(buildPremiumProductSeoFromId(premiumProductMatch[1]))
  }

  if (normalizedPath.startsWith('/edit-property/')) {
    return resolveSeo({ ...STATIC_PAGE_SEO.postProperty, noIndex: true, noFollow: true })
  }

  if (normalizedPath === PATHS.privacy) {
    const doc = legalFallback('privacy')
    return resolveSeo(STATIC_PAGE_SEO.privacy(doc.title, doc.description))
  }

  if (normalizedPath === PATHS.terms) {
    const doc = legalFallback('terms')
    return resolveSeo(STATIC_PAGE_SEO.terms(doc.title, doc.description))
  }

  if (normalizedPath === PATHS.properties || normalizedPath === PATHS.propertyList || normalizedPath === PATHS.propertyListing) {
    return buildPropertiesRouteSeo(search)
  }

  // Phase-1 SEO landings: prefer clean canonical; extra query → noindex (React PageSeo also sets this).
  if (isSeoLandingPathname(normalizedPath)) {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    const hasFilters = [...params.keys()].length > 0
    return resolveSeo({
      title: 'Properties | Quick Build Con',
      description: 'Browse verified property listings on Quick Build Con.',
      canonical: absoluteUrl(normalizedPath),
      noIndex: hasFilters,
      noFollow: false,
    })
  }

  const dashboardTitle = DASHBOARD_ROUTES[normalizedPath]
  if (dashboardTitle) {
    return resolveSeo(buildPrivateDashboardSeo(dashboardTitle))
  }

  if (normalizedPath.startsWith('/premium-package/payment-')) {
    return resolveSeo({ ...STATIC_PAGE_SEO.premiumProducts, noIndex: true, noFollow: true })
  }

  const staticSeo = STATIC_ROUTE_SEO[normalizedPath]
  if (staticSeo) {
    return resolveSeo(staticSeo())
  }

  if (normalizedPath === '/404') {
    return resolveSeo({ ...STATIC_PAGE_SEO.notFound, canonical: absoluteUrl('/404') })
  }

  // Unknown routes must not inherit homepage title/canonical from the SPA shell.
  return resolveSeo({
    ...STATIC_PAGE_SEO.notFound,
    canonical: absoluteUrl(normalizedPath),
  })
}
